import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FaEye, FaSearch, FaCalendar, FaUser, FaBuilding, FaTimes, FaDownload } from 'react-icons/fa';

interface WorkerRemovalRecord {
  id: string;
  taskId: string;
  taskTitle: string;
  projectId: string;
  projectName: string;
  businessId: string;
  businessName: string;
  workerRemoved: {
    uid: string;
    name: string;
    email: string;
  };
  removedBy: {
    uid: string;
    email: string;
    role: string;
  };
  removalReason: string;
  previousAssignment: {
    finalPrice: number | null;
    finalPriceUnit: string | null;
    finalDuration: number | null;
    finalTotal: number | null;
    assignmentRequestId: string | null;
  };
  timestamp: string;
  createdAt: string;
}

const WorkerRemovalHistory: React.FC = () => {
  const [removalHistory, setRemovalHistory] = useState<WorkerRemovalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBusiness, setFilterBusiness] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [selectedRecord, setSelectedRecord] = useState<WorkerRemovalRecord | null>(null);

  useEffect(() => {
    fetchRemovalHistory();
  }, []);

  const fetchRemovalHistory = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'workerRemovalHistory'),
        orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const records: WorkerRemovalRecord[] = [];
      
      querySnapshot.forEach((doc) => {
        records.push({ id: doc.id, ...doc.data() } as WorkerRemovalRecord);
      });
      
      setRemovalHistory(records);
    } catch (error) {
      console.error('Error fetching removal history:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredHistory = removalHistory.filter(record => {
    const matchesSearch = 
      record.taskTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.workerRemoved.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.workerRemoved.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.projectName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesBusiness = !filterBusiness || record.businessId === filterBusiness;
    
    const matchesDate = !filterDate || 
      new Date(record.timestamp).toDateString() === new Date(filterDate).toDateString();
    
    return matchesSearch && matchesBusiness && matchesDate;
  });

  const exportToCSV = () => {
    const headers = [
      'Fecha',
      'Proyecto',
      'Tarea',
      'Business',
      'Worker Removido',
      'Email Worker',
      'Removido Por',
      'Precio Anterior',
      'Total Anterior',
      'Razón'
    ];
    
    const csvData = filteredHistory.map(record => [
      new Date(record.timestamp).toLocaleString(),
      record.projectName,
      record.taskTitle,
      record.businessName,
      record.workerRemoved.name,
      record.workerRemoved.email,
      record.removedBy.email,
      record.previousAssignment.finalPrice || 'N/A',
      record.previousAssignment.finalTotal || 'N/A',
      record.removalReason
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `worker_removal_history_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getBusinesses = () => {
    const businesses = new Set(removalHistory.map(record => record.businessId));
    return Array.from(businesses).map(businessId => {
      const record = removalHistory.find(r => r.businessId === businessId);
      return { id: businessId, name: record?.businessName || businessId };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando historial...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Historial de Workers Removidos</h1>
              <p className="text-gray-600 mt-1">Auditoría completa de remociones de workers</p>
            </div>
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2"
            >
              <FaDownload />
              Exportar CSV
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por tarea, worker, business..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Business</label>
              <select
                value={filterBusiness}
                onChange={(e) => setFilterBusiness(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">Todos los businesses</option>
                {getBusinesses().map(business => (
                  <option key={business.id} value={business.id}>
                    {business.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha</label>
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterBusiness('');
                  setFilterDate('');
                }}
                className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <FaTimes />
                Limpiar Filtros
              </button>
            </div>
          </div>
        </div>

        {/* Tabla de historial */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Registros ({filteredHistory.length})
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Proyecto/Tarea
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Business
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Worker Removido
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Precio Anterior
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Removido Por
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredHistory.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <FaCalendar className="text-gray-400 mr-2" />
                        {new Date(record.timestamp).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {record.projectName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {record.taskTitle}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FaBuilding className="text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{record.businessName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FaUser className="text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {record.workerRemoved.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {record.workerRemoved.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.previousAssignment.finalPrice ? (
                        <div>
                          <div>${record.previousAssignment.finalPrice} {record.previousAssignment.finalPriceUnit}</div>
                          <div className="text-gray-500">Total: ${record.previousAssignment.finalTotal}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.removedBy.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => setSelectedRecord(record)}
                        className="text-red-600 hover:text-red-900 flex items-center gap-1"
                      >
                        <FaEye />
                        Ver Detalles
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredHistory.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📝</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay registros</h3>
              <p className="text-gray-500">No se encontraron registros de workers removidos con los filtros actuales.</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de detalles */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Detalles de Remoción</h2>
                <button
                  onClick={() => setSelectedRecord(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Información del Proyecto</h3>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Proyecto:</span>
                      <div className="text-sm text-gray-900">{selectedRecord.projectName}</div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Tarea:</span>
                      <div className="text-sm text-gray-900">{selectedRecord.taskTitle}</div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Business:</span>
                      <div className="text-sm text-gray-900">{selectedRecord.businessName}</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Worker Removido</h3>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Nombre:</span>
                      <div className="text-sm text-gray-900">{selectedRecord.workerRemoved.name}</div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Email:</span>
                      <div className="text-sm text-gray-900">{selectedRecord.workerRemoved.email}</div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">ID:</span>
                      <div className="text-sm text-gray-900 font-mono">{selectedRecord.workerRemoved.uid}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Asignación Anterior</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Precio:</span>
                      <div className="text-sm text-gray-900">
                        {selectedRecord.previousAssignment.finalPrice 
                          ? `$${selectedRecord.previousAssignment.finalPrice} ${selectedRecord.previousAssignment.finalPriceUnit}`
                          : 'N/A'
                        }
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Duración:</span>
                      <div className="text-sm text-gray-900">
                        {selectedRecord.previousAssignment.finalDuration || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Total:</span>
                      <div className="text-sm text-gray-900">
                        {selectedRecord.previousAssignment.finalTotal ? `$${selectedRecord.previousAssignment.finalTotal}` : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Request ID:</span>
                      <div className="text-sm text-gray-900 font-mono">
                        {selectedRecord.previousAssignment.assignmentRequestId || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Información de Remoción</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Removido por:</span>
                    <div className="text-sm text-gray-900">{selectedRecord.removedBy.email}</div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Rol:</span>
                    <div className="text-sm text-gray-900">{selectedRecord.removedBy.role}</div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Razón:</span>
                    <div className="text-sm text-gray-900">{selectedRecord.removalReason}</div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Fecha y hora:</span>
                    <div className="text-sm text-gray-900">
                      {new Date(selectedRecord.timestamp).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerRemovalHistory; 